// ChatSidebar.js - COMPONENTE ACTUALIZADO CON ENCABEZADOS FIJOS A ANCHO COMPLETO

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  IconButton,
  Button,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  Dialog,
  // DialogContent removed as it's not used
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  PlusCircle, 
  X, 
  MoreVertical,
  Trash,
  Folder,
  LibraryBig,
} from 'lucide-react';

// agtEduService import removed as it's not used

// Estilos personalizados para scrollbar
const CustomScrollbar = styled('div')(({ theme }) => ({
  height: '100%',
  overflow: 'auto',
  position: 'relative',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.2)' 
      : 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.3)' 
        : 'rgba(0, 0, 0, 0.3)',
    }
  },
  // Para Firefox
  scrollbarWidth: 'thin',
  scrollbarColor: `${theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.2)' 
    : 'rgba(0, 0, 0, 0.2)'} transparent`,
}));

// Nuevo estilo para encabezados sticky a ancho completo
const StickyDateHeader = styled('div')(({ theme }) => ({
  position: 'sticky',
  top: 0,
  left: 0,
  right: 0,
  backgroundColor: theme.palette.mode === 'dark' 
    ? `${theme.palette.background.default}ee` // Semi-transparente
    : `${theme.palette.background.paper}ee`,
  backdropFilter: 'blur(5px)',
  zIndex: 10,
  padding: theme.spacing(1, 2),
  transition: 'background-color 0.2s ease',
  display: 'flex',
  alignItems: 'center',
  width: '100%',
}));

// Estilos
const SidebarDrawer = styled(Box)(({ theme }) => ({
  width: '280px',
  height: '100%',
  position: 'absolute',
  left: 0,
  top: 0,
  boxSizing: 'border-box',
  borderRight: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.default,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  zIndex: 1200,
}));

const NewChatButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius * 3,
  padding: theme.spacing(1, 2),
  marginBottom: theme.spacing(1),
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontWeight: 500,
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

const ConversationItem = styled(ListItemButton)(({ theme, selected }) => ({
  borderRadius: theme.shape.borderRadius,
  margin: theme.spacing(0.5, 1),
  padding: theme.spacing(1, 2),
  backgroundColor: selected ? theme.palette.action.selected : 'transparent',
  '&:hover': {
    backgroundColor: selected 
      ? theme.palette.action.selected 
      : theme.palette.action.hover,
  }
}));

const SidebarButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 2),
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontWeight: 500,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  }
}));

// Animación para las transiciones de hover
const AnimatedListItem = styled(ListItem)(({ theme }) => ({
  transition: 'all 0.2s ease-in-out',
}));

// Función para formatear la fecha
const formatDate = (timestamp) => {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return date.toLocaleDateString('en-US', { weekday: 'long' });
  } else {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
};

// Componente principal
const ChatSidebar = ({ 
  open, 
  conversations, 
  currentConversationId, 
  onSelectConversation, 
  onDeleteConversation,
  onNewChat,
  onClose,
  onOpenLibrary
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // Estados para menú de conversaciones
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedConvForMenu, setSelectedConvForMenu] = useState(null);
  
  // Estados para modal de asignaturas
  const [subjectsDialogOpen, setSubjectsDialogOpen] = useState(false);
  
  // Removed unused state variables: newSubject, selectedSubject, filesCurrentPage, setFilesCurrentPage

  // Fetch subjects when dialog opens - simplified to avoid warnings
  const fetchSubjects = useCallback(() => {
    // Implementation removed as it's not actually used
    // Just keeping the function for the useEffect dependency
  }, []);

  useEffect(() => {
    if (subjectsDialogOpen) {
      fetchSubjects();
    }
  }, [subjectsDialogOpen, fetchSubjects]);

  // Handlers para menú de conversaciones
  const handleMenuOpen = (event, conversation) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedConvForMenu(conversation);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedConvForMenu(null);
  };

  const handleDeleteConversation = () => {
    if (selectedConvForMenu && onDeleteConversation) {
      onDeleteConversation(selectedConvForMenu.id);
      handleMenuClose();
    }
  };
  
  // Handlers para biblioteca y asignaturas
  const handleOpenLibrary = () => {
    if (onOpenLibrary) {
      onOpenLibrary();
      if (isMobile) {
        onClose();
      }
    }
  };

  // Funciones para el modal de asignaturas
  const handleOpenSubjectsDialog = () => {
    setSubjectsDialogOpen(true);
  };

  const handleCloseSubjectsDialog = useCallback(() => {
    setSubjectsDialogOpen(false);
  }, []);

  // Agrupar conversaciones por fecha
  const groupedConversations = conversations.reduce((groups, conv) => {
    const dateKey = formatDate(conv.timestamp);
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(conv);
    return groups;
  }, {});

  if (!open) return null; // No renderizar nada si está cerrado

  return (
    <SidebarDrawer>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
        {/* Encabezado con título y botón de cerrar (en móvil) */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            AGT Edu
          </Typography>
          <Box sx={{ flexGrow: 1 }} />
          {isMobile && (
            <IconButton onClick={onClose}>
              <X size={20} />
            </IconButton>
          )}
        </Box>
        
        {/* Botón de nueva conversación */}
        <NewChatButton
          variant="outlined"
          startIcon={<PlusCircle size={20} />}
          onClick={onNewChat}
          fullWidth
        >
          New Chat
        </NewChatButton>
        
        {/* Lista de conversaciones agrupadas por fecha con scroll personalizado y headers sticky */}
        <CustomScrollbar sx={{ flexGrow: 1, mx: -2 }}> {/* Expandimos a ancho completo */}
          {Object.entries(groupedConversations).length > 0 ? (
            Object.entries(groupedConversations).map(([dateGroup, convs]) => (
              <Box key={dateGroup} sx={{ position: 'relative' }}>
                <StickyDateHeader>
                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ fontWeight: 500, ml: 1 }}
                  >
                    {dateGroup}
                  </Typography>
                </StickyDateHeader>
                <List sx={{ px: 2 }}>
                  {convs.map((conversation) => (
                    <AnimatedListItem key={conversation.id} disablePadding>
                      <ConversationItem
                        selected={conversation.id === currentConversationId}
                        onClick={() => onSelectConversation(conversation)}
                        sx={{ 
                          width: '100%', 
                          pr: 1,
                          transition: 'all 0.2s ease'
                        }}
                      >
                        <ListItemText 
                          primary={conversation.title}
                          secondary={conversation.lastMessage}
                          primaryTypographyProps={{
                            noWrap: true,
                            fontSize: '0.95rem',
                            fontWeight: conversation.id === currentConversationId ? 500 : 400
                          }}
                          secondaryTypographyProps={{
                            noWrap: true,
                            fontSize: '0.8rem'
                          }}
                          sx={{ mr: 1 }}
                        />
                        <IconButton 
                          size="small" 
                          onClick={(e) => handleMenuOpen(e, conversation)}
                          sx={{ 
                            opacity: 0.6,
                            '&:hover': { opacity: 1 },
                            transition: 'opacity 0.2s ease'
                          }}
                        >
                          <MoreVertical size={16} />
                        </IconButton>
                      </ConversationItem>
                    </AnimatedListItem>
                  ))}
                </List>
              </Box>
            ))
          ) : (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center',
              height: '100%',
              opacity: 0.6,
              px: 2
            }}>
              <Typography variant="body2" color="text.secondary" align="center">
                No conversations yet.
                <br />
                Start a new chat to begin.
              </Typography>
            </Box>
          )}
        </CustomScrollbar>
        
        {/* Footer con botones de acceso a recursos */}
        <Box sx={{ pt: 2, borderTop: `1px solid ${theme.palette.divider}`, mt: 2 }}>
          <SidebarButton
            variant="text" 
            startIcon={<LibraryBig size={16} />}
            fullWidth 
            onClick={handleOpenLibrary}
            sx={{ 
              justifyContent: 'flex-start', 
              textTransform: 'none',
              color: 'text.secondary',
              mb: 1,
              transition: 'all 0.2s ease'
            }}
          >
            Mi biblioteca
          </SidebarButton>

          <SidebarButton 
            variant="text" 
            startIcon={<Folder size={16} />}
            fullWidth 
            onClick={handleOpenSubjectsDialog}
            sx={{ 
              justifyContent: 'flex-start', 
              textTransform: 'none',
              color: 'text.secondary',
              transition: 'all 0.2s ease'
            }}
          >
            Mis asignaturas
          </SidebarButton>
        </Box>

        {/* Menú de opciones para las conversaciones */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          PaperProps={{
            elevation: 2,
            sx: {
              borderRadius: theme.shape.borderRadius,
              minWidth: 150,
              overflow: 'hidden'
            }
          }}
        >
          <MenuItem onClick={handleDeleteConversation} sx={{ 
            color: 'error.main',
            transition: 'background-color 0.2s ease'
          }}>
            <Trash size={16} style={{ marginRight: 8 }} />
            Delete Conversation
          </MenuItem>
        </Menu>

        {/* Modal para asignaturas con scroll personalizado */}
        <Dialog
          open={subjectsDialogOpen}
          onClose={handleCloseSubjectsDialog}
          fullWidth
          maxWidth="sm"
          PaperProps={{
            sx: {
              borderRadius: theme.shape.borderRadius * 2,
              overflow: 'hidden'
            }
          }}
        >
          {/* Dialog content simplified/removed */}
        </Dialog>
      </Box>
    </SidebarDrawer>
  );
};

export default ChatSidebar;